import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CssBaseline from '@mui/material/CssBaseline';
import CalculateIcon from '@mui/icons-material/Calculate';
import ScienceIcon from '@mui/icons-material/Science';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AirIcon from '@mui/icons-material/Air';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AppNavbar from '../dashboard/components/AppNavbar';
import SideMenu from '../dashboard/components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { DataModeProvider, DATA_MODES } from '../context/DataModeContext';
import { GrowProvider } from '../dashboard/context/GrowContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// pH Adjustment Calculator
function PHCalculator() {
  const [currentPH, setCurrentPH] = React.useState(7.0);
  const [targetPH, setTargetPH] = React.useState(6.0);
  const [reservoirSize, setReservoirSize] = React.useState(20);
  const [result, setResult] = React.useState(null);

  const calculate = () => {
    const phDiff = currentPH - targetPH;
    // Simplified calculation - actual calculation would depend on buffer capacity
    const mlPhDown = phDiff > 0 ? Math.abs(phDiff) * reservoirSize * 0.5 : 0;
    const mlPhUp = phDiff < 0 ? Math.abs(phDiff) * reservoirSize * 0.3 : 0;
    setResult({
      direction: phDiff > 0 ? 'down' : 'up',
      amount: phDiff > 0 ? mlPhDown : mlPhUp,
      recommendation: phDiff > 0
        ? `Add approximately ${mlPhDown.toFixed(1)} ml of pH Down`
        : `Add approximately ${mlPhUp.toFixed(1)} ml of pH Up`,
    });
  };

  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <ScienceIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
          pH Adjustment Calculator
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Current pH"
            type="number"
            value={currentPH}
            onChange={(e) => setCurrentPH(parseFloat(e.target.value))}
            fullWidth
            size="small"
            inputProps={{ step: 0.1, min: 0, max: 14 }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Target pH"
            type="number"
            value={targetPH}
            onChange={(e) => setTargetPH(parseFloat(e.target.value))}
            fullWidth
            size="small"
            inputProps={{ step: 0.1, min: 0, max: 14 }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Reservoir Size (gallons)"
            type="number"
            value={reservoirSize}
            onChange={(e) => setReservoirSize(parseFloat(e.target.value))}
            fullWidth
            size="small"
          />
        </Grid>
      </Grid>
      <Button
        variant="contained"
        onClick={calculate}
        sx={{ mt: 2, textTransform: 'none' }}
        startIcon={<CalculateIcon />}
      >
        Calculate
      </Button>
      {result && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {result.recommendation}
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Note: Add slowly and retest. Actual amounts may vary based on water chemistry.
          </Typography>
        </Alert>
      )}
    </Card>
  );
}

// EC/PPM Calculator
function ECCalculator() {
  const [currentEC, setCurrentEC] = React.useState(1.0);
  const [targetEC, setTargetEC] = React.useState(1.8);
  const [reservoirSize, setReservoirSize] = React.useState(20);

  const ecDiff = targetEC - currentEC;
  const ppmDiff = ecDiff * 500; // Using 500 scale

  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <WaterDropIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
          EC/PPM Calculator
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Current EC (mS/cm)"
            type="number"
            value={currentEC}
            onChange={(e) => setCurrentEC(parseFloat(e.target.value))}
            fullWidth
            size="small"
            inputProps={{ step: 0.1 }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Target EC (mS/cm)"
            type="number"
            value={targetEC}
            onChange={(e) => setTargetEC(parseFloat(e.target.value))}
            fullWidth
            size="small"
            inputProps={{ step: 0.1 }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Reservoir Size (gallons)"
            type="number"
            value={reservoirSize}
            onChange={(e) => setReservoirSize(parseFloat(e.target.value))}
            fullWidth
            size="small"
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={4}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>EC Difference</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: ecDiff > 0 ? 'success.main' : 'error.main' }}>
            {ecDiff > 0 ? '+' : ''}{ecDiff.toFixed(2)} mS/cm
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>PPM Difference (500 scale)</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: ppmDiff > 0 ? 'success.main' : 'error.main' }}>
            {ppmDiff > 0 ? '+' : ''}{ppmDiff.toFixed(0)} ppm
          </Typography>
        </Box>
      </Stack>
      {ecDiff > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Add nutrients gradually to increase EC. Check manufacturer guidelines for dosing.
        </Alert>
      )}
      {ecDiff < 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          EC is too high. Add fresh water to dilute, or perform a partial reservoir change.
        </Alert>
      )}
    </Card>
  );
}

// VPD Calculator
function VPDCalculator() {
  const [temp, setTemp] = React.useState(75);
  const [humidity, setHumidity] = React.useState(60);
  const [leafOffset, setLeafOffset] = React.useState(2);

  // VPD calculation
  const tempC = (temp - 32) * 5/9;
  const leafTempC = tempC - leafOffset;
  const svpAir = 0.6108 * Math.exp((17.27 * tempC) / (tempC + 237.3));
  const svpLeaf = 0.6108 * Math.exp((17.27 * leafTempC) / (leafTempC + 237.3));
  const vpd = svpLeaf - (svpAir * humidity / 100);

  const getVPDStatus = (vpd) => {
    if (vpd < 0.4) return { label: 'Too Low - Risk of mold', color: 'error' };
    if (vpd < 0.8) return { label: 'Low - Good for propagation', color: 'warning' };
    if (vpd < 1.2) return { label: 'Optimal for vegetative', color: 'success' };
    if (vpd < 1.6) return { label: 'Optimal for flowering', color: 'success' };
    return { label: 'Too High - Risk of stress', color: 'error' };
  };

  const status = getVPDStatus(vpd);

  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <AirIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
          VPD Calculator
        </Typography>
      </Stack>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Air Temperature: {temp}°F
          </Typography>
          <Slider
            value={temp}
            onChange={(e, v) => setTemp(v)}
            min={60}
            max={95}
            valueLabelDisplay="auto"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Humidity: {humidity}%
          </Typography>
          <Slider
            value={humidity}
            onChange={(e, v) => setHumidity(v)}
            min={20}
            max={90}
            valueLabelDisplay="auto"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Leaf Temp Offset: -{leafOffset}°F
          </Typography>
          <Slider
            value={leafOffset}
            onChange={(e, v) => setLeafOffset(v)}
            min={0}
            max={10}
            valueLabelDisplay="auto"
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={4} alignItems="center">
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Current VPD</Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
            {vpd.toFixed(2)} kPa
          </Typography>
        </Box>
        <Alert severity={status.color} sx={{ flex: 1 }}>
          {status.label}
        </Alert>
      </Stack>
    </Card>
  );
}

// Growing Tips Section
function GrowingTips() {
  const tips = [
    { icon: <ScienceIcon />, title: 'pH Fluctuations', description: 'If pH swings rapidly, check for root issues or nutrient lockout. Healthy roots maintain stable pH.' },
    { icon: <WaterDropIcon />, title: 'EC Rising', description: 'Rising EC without adding nutrients indicates plants are drinking more water than nutrients. Add fresh water.' },
    { icon: <ThermostatIcon />, title: 'Heat Stress Signs', description: 'Curling leaves, bleaching, or foxtailing? Lower temps or increase distance from lights.' },
    { icon: <AirIcon />, title: 'VPD Sweet Spot', description: 'Keep VPD between 0.8-1.2 kPa for veg, 1.0-1.5 kPa for flower for optimal transpiration.' },
  ];

  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <TipsAndUpdatesIcon sx={{ color: 'warning.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
          Growing Tips & Troubleshooting
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        {tips.map((tip, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack direction="row" spacing={1.5}>
                <Box sx={{ color: 'primary.main' }}>{tip.icon}</Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{tip.title}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {tip.description}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
}

function AnalyticsContent() {
  const [activeTab, setActiveTab] = React.useState(0);

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1200px' }, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
          Analytics & Calculators
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Tools to help you optimize your grow and troubleshoot issues
        </Typography>
      </Stack>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<CalculateIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Calculators"
          sx={{ textTransform: 'none' }}
        />
        <Tab
          icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Grow Analysis"
          sx={{ textTransform: 'none' }}
        />
        <Tab
          icon={<TipsAndUpdatesIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Tips & Guides"
          sx={{ textTransform: 'none' }}
        />
      </Tabs>

      {/* Calculators Tab */}
      {activeTab === 0 && (
        <Stack spacing={3}>
          <PHCalculator />
          <ECCalculator />
          <VPDCalculator />
        </Stack>
      )}

      {/* Grow Analysis Tab */}
      {activeTab === 1 && (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <TrendingUpIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Grow Analysis Coming Soon
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 400, mx: 'auto' }}>
            Detailed analytics and insights about your grow performance, including trends,
            comparisons, and recommendations based on your sensor data.
          </Typography>
        </Card>
      )}

      {/* Tips Tab */}
      {activeTab === 2 && (
        <GrowingTips />
      )}
    </Box>
  );
}

export default function AnalyticsPage(props) {
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <DataModeProvider mode={isDemo ? DATA_MODES.DEMO : DATA_MODES.LIVE}>
          <GrowProvider>
            <SideMenu />
            <AppNavbar />
            <Box
              component="main"
              sx={(theme) => ({
                flexGrow: 1,
                backgroundColor: theme.vars
                  ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                  : alpha(theme.palette.background.default, 1),
                overflow: 'auto',
                minHeight: '100vh',
              })}
            >
              <Stack
                sx={{
                  pt: { xs: 10, md: 3 },
                  pb: 5,
                }}
              >
                <AnalyticsContent />
              </Stack>
            </Box>
          </GrowProvider>
        </DataModeProvider>
      </Box>
    </AppTheme>
  );
}
